import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { CognitoIdentityProvider, ForgetDeviceCommand } from '@aws-sdk/client-cognito-identity-provider';
import { GetParameterCommand, SSM, SSMClient } from '@aws-sdk/client-ssm';
import { StorageService } from '../services/storage.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { CloudService } from '../../shared/services/cloud.service';
import { ModulePermissions } from '../models/permissions.model';
import FingerprintJS from '@fingerprintjs/fingerprintjs/dist/fp.esm';
import { CasedashboardService } from 'src/app/case-dashboard/casedashboard.service';
import { EventType } from '@shared/helpers/constants';
import { ApiErrorPopUpComponent } from '@shared/api-error-pop-up/api-error-pop-up.component';
import { MatDialog } from '@angular/material/dialog';
import { SharedStateService } from './shared-state.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private session: any;
  private modulePermission: ModulePermissions[];
  private modulePermissionModel: ModulePermissions;
  private storeModulePermission: ModulePermissions[]
  public tokenExpired: any;
  public cognitoIdToken: any;
  public authenticated: any;
  public decodedIdToken: any;
  getItemRes: any;
  signOutResponse: any;
  eventOutcome: number;
  actionActivityDetails: string;
  errOutcome: number = 0;
  successOutcome: number = 1;
  errUserDetails = {};
  errorLog: string;
  successLog: string;
  constructor(
    private storageService: StorageService,
    private router: Router,
    private cloudService: CloudService,
    private casedashboardService: CasedashboardService,
    public dialog: MatDialog,
    private sharedService: SharedStateService
      ) {
      }
  /**
   * (description): Retrieves the item's string representation from the local storage
   * (parameter) : string
   * (returns) : string
   * (memberof) : AuthService
   */
  public getItem(key: string): any {
    this.getItemRes = localStorage.getItem(key);
    return JSON.parse(localStorage.getItem(key) || '{}');
  }
  /**
 * (description): Removes the item's string representation from the local storage
 * (parameter) : string
 * (returns) : string
 * (memberof) : AuthService
 */
  public removeItem(key: string): any {
    localStorage.removeItem(key);
  }

  async getVisitorId() {
    return new Promise<string>(async (resolve, reject) => {
      try {
        const fpPromise = FingerprintJS.load();
        const fp = await fpPromise;
        const result = await fp.get();
        localStorage.setItem('visitorId', result.visitorId)
        resolve(result.visitorId)
      }
      catch (e) {
        reject(e);
      }
    });
  }

  /*
   * @description Sets the item in the local storage
   * @param {string} key
   * @param {*} value
   * @memberof AuthService
   */
  public setItem(key: string, value: any): void {
    if (typeof value === 'string') {
      localStorage.setItem(key, value);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }
  /*
   * @description Decode the JWT token
   *
   * @returns
   * @memberof AuthService
   */
  decode(idToken?: string): any {
    if (!!idToken && idToken !== undefined) {
      this.decodedIdToken = jwt_decode(idToken);
    }
    return this.decodedIdToken;
  }
  public getModulePermission(): ModulePermissions[] {
    let snPermission;
    if (this.modulePermission) {
      snPermission = this.modulePermission;
      this.cloudService.getSnPermission = snPermission;
    } else if (this.cloudService.getSnPermission !== undefined) {
      snPermission = this.cloudService.getSnPermission;
      this.cloudService.getSnPermission = snPermission;
    }
    this.modulePermission = snPermission;
    return this.modulePermission;
  }
  setModulePermission(modulePermission: ModulePermissions[]): void {
    this.cloudService.getSnPermission = modulePermission;
    this.modulePermission = modulePermission;
  }
  /**
   * (description): Refresh the token
   * (memberof) : AuthService
   */
  public refreshToken(requestUrl = ''): any {
    let accessToken = '';
    return new Promise((resolve, reject) => {
      this.cloudService.currentSession()
        .then((session: any) => {
          this.session = session;
          const idTokenExpire = session.getIdToken().getExpiration();
          const refreshToken = session.getRefreshToken();
          const currentTimeSeconds = Math.round(+new Date() / 1000);
          if (idTokenExpire < currentTimeSeconds) {
            // in case of expired use the existing token when user closes browser and reopens
            accessToken = session.getAccessToken().getJwtToken();
            resolve(accessToken);
          } else {
            this.cloudService.currentAuthenticatedUser()
              .then((res: any) => {
                res.refreshSession(refreshToken, (err: any, data: any) => {
                  if (err) {
                    reject(err);
                  } else {
                    accessToken = data.getAccessToken().getJwtToken();
                    resolve(accessToken);
                  }
                });
              });
            accessToken = session.getAccessToken().getJwtToken();
            resolve(accessToken);
          }
        })
        .catch(() => {
          // No logged-in user: don't set auth header
          resolve(accessToken);
        });
    });
  }
  /**
   * (description): check for expiration and if token is still existing or not
   * (returns) : boolean
   * (memberof) : AuthService
   */
  isAuthenticated(): boolean {
    this.authenticated = false;
    this.authenticated = !!this.getCognitoIdTokenFromStorage() && !this.isTokenExpired();
    return this.authenticated;
  }
  /**
   * (description): simulate jwt token is valid
   * (returns) : boolean
   * (memberof) : AuthService
   */
  isTokenExpired(): boolean {
    this.tokenExpired = false;
    return false;
  }
  /**
   * (description): Method returns the ID token
   * (returns) : string
   * (memberof) : AuthService
   */
  getCognitoIdTokenFromStorage(): string {
    let cognitoIdToken;
    for (const key in localStorage) {
      if (key.includes('clpSession')) {
        const session = JSON.parse(localStorage.getItem('clpSession') || '{}');
        cognitoIdToken = session.idToken;
      }
    }
    return cognitoIdToken;
  }
  /**
   * (description): Method returns the Access token
   * (returns) : string
   * (memberof) : AuthService
   */
  getCognitoAccessTokenFromStorage(): string {
    let cognitoAccessToken: any;
    if (!!this.session) {
      cognitoAccessToken = this.session.getAccessToken().getJwtToken();
    } else {
      for (const key in localStorage) {
        if (key.includes('clpSession')) {
          const session =  JSON.parse(localStorage.getItem('clpSession') || '{}');
          cognitoAccessToken = session.accessToken;
        }
      }
    }
    return cognitoAccessToken;
  }
  /**
   * (description): Method to signout current user.
   * (returns) : string
   * (memberof) : AuthService
   */
  public async signOut() {
    const key = 'device_key';
    const decodedAccesssToken = this.decode(this.getCognitoAccessTokenFromStorage());
    const deviceKey = decodedAccesssToken ? decodedAccesssToken[key] : '';

    const credentials = fromCognitoIdentityPool({
      identityPoolId: environment.identityPoolId,
      logins: {
        [environment.userPoolAwsLogin]: this.getCognitoIdTokenFromStorage(),
      },
      clientConfig: {
        region: environment.region,
      }
    });

    const client = new CognitoIdentityProvider({ region: environment.region, credentials });

    try {
      if(deviceKey != null && deviceKey != undefined){

        const forgetDeviceCommand = new ForgetDeviceCommand({
          AccessToken: this.getCognitoAccessTokenFromStorage(),
          DeviceKey: deviceKey,
        });
        await client.send(forgetDeviceCommand);
        this.authServiceSignout();
      }else{

        this.authServiceSignout();
      }
    } catch (error) {
        console.error("Error signing out:", error);
      }
  }
  /**
   * (description): This method is called internally for signout
   * (memberof) : AuthService
   */
  public authServiceSignout(): void {
    this.cloudService.amplifySignOut()
      .subscribe(
        (response) => {
          this.logout();
          this.signOutResponse = response;
        },
        (error) => {
          this.logout();
        }
      );
  }

  /**
   * (description): This method is used to clear anything that needs to be removed
   * (memberof) : AuthService
   */
  clear(): void {
    this.session = undefined;
    for (const key in localStorage) {
      if (key.includes('CognitoIdentity') && key.includes('clpSession')) {
        localStorage.removeItem(key);
      }
    }
    const keys = ['AccessToken', 'IdToken', 'userDetails', 'userInfo', 'clpSession', '.accessToken', '.idToken', 'visitorId'];
    keys.forEach(key => localStorage.removeItem(key));
    this.cognitoIdToken = null;
  }
  /**
   * (description): This method is used to clear local storage and also the route to login
   * (memberof) : AuthService
   */
  logout(): void {
    this.storageService.clearAll();
    this.clear();
    let timeoutState: string = '';
     this.sharedService.idleTimeoutListener$.subscribe((state) => {
       timeoutState = state;
     });
    const DELAY = timeoutState && timeoutState === 'TIMEOUT' ? 3000 : 0;
    let url = new URL(environment.cloudFrontCLPSignOutUrl);
    if (localStorage.getItem('checkInactivity') === 'true') {
      url.searchParams.set('inactive', 'true');
    } else {
      url.searchParams.set('inactive', 'false');
    }
    if (DELAY) {
      this.sharedService.idleTimeoutUnsubscribe();
      setTimeout(() => window.open(url.toString(), "_self"), DELAY);
    } else {
      window.open(url.toString(), "_self");
    }
  }
  /**
     * (description): This method is used to clear local storage and also the route to login
     * (memberof) : AuthService
     */
  exitToApp(): void {
    this.clear();
    window.open(environment.cloudFrontCLPExitAppUrl, "_self");
  }
  public async setCognitoConfigObject(): Promise<any> {
    try {
      const userPoolConfig: string = environment.userPoolAwsLogin;
      const loginObj: any = {};
      loginObj[userPoolConfig] = this.getCognitoIdTokenFromStorage();

      const credentials = // JS SDK v3 switched credential providers from classes to functions.
      // This is the closest approximation from codemod of what your application needs.
      // Reference: https://www.npmjs.com/package/@aws-sdk/credential-providers
      fromCognitoIdentityPool({
        identityPoolId: environment.identityPoolId,
        logins: loginObj,
        clientConfig: {
          region: environment.region,
        }
      });

      // No refresh needed in v3, credentials are automatically updated
      const config = {
        region: environment.region,
        credentials,
      };

      // No need to return AWS object directly
      return config;
    } catch (err) {
      return err;
    }
  }


  async deleteCognitoUser(userName, callback): Promise<any> {
    const value = await this.setCognitoConfigObject();

    const deleteParams = {
      UserPoolId: environment.userPoolId,
      Username: userName,
    };
    const cognito = new CognitoIdentityProvider({ region: environment.region, credentials: value.credentials });

    cognito.adminDeleteUser(deleteParams, (err, data) => {
      if (err) {
        this.errorLog = 'err in delete user' + JSON.stringify(err);
        return callback(err, null);
      }
      else {
        this.successLog = 'success in delete user' + JSON.stringify(data);
        return callback(null, data);
      }
    });
  }

  async removeAdminFromAdminGroup(userName, callback): Promise<any> {
    const value = await this.setCognitoConfigObject();
    const params = {
      GroupName: 'ic-admin',
      UserPoolId: environment.userPoolId,
      Username: userName
    };

    const cognito = new CognitoIdentityProvider({ region: environment.region, credentials: value.credentials });
    cognito.adminRemoveUserFromGroup(params, (err, data) => {
      if (err) {
        this.errorLog = 'remove user from group error ' + JSON.stringify(err);
        return callback(err, null);
      }
      else {
        this.successLog = 'remove user from group success ' + JSON.stringify(data);
        return callback(null, data);
      }
    });
  }

  async addAdminToUserGroup(userName, callback): Promise<any> {
    const value = await this.setCognitoConfigObject();
    const params = {
      GroupName: 'ic-user',
      UserPoolId: environment.userPoolId,
      Username: userName
    };
    const cognito = new CognitoIdentityProvider({ region: environment.region, credentials: value.credentials });
    cognito.adminAddUserToGroup(params, (err, data) => {
      if (err) {
        this.errorLog = 'admin change error ' + JSON.stringify(err);
        return callback(err, null);
      }
      else {
        this.successLog = 'admin change success ' + JSON.stringify(data);
        return callback(null, data);
      }
    });
  }
  async removeUserFromGroup(userName, callback): Promise<any> {
    const value = await this.setCognitoConfigObject();
    const params = {
      GroupName: 'ic-user',
      UserPoolId: environment.userPoolId,
      Username: userName
    };
    const cognito = new CognitoIdentityProvider({ region: environment.region, credentials: value.credentials });
    cognito.adminRemoveUserFromGroup(params, (err, data) => {
      if (err) {
        this.errorLog = 'remove user from group error ' + JSON.stringify(err);
        return callback(err, null);
      }
      else {
        this.successLog = 'remove user from group success ' + JSON.stringify(data);
        return callback(null, data);
      }
    });
  }
  async addUserToAdminGroup(userName, callback): Promise<any> {
    const value = await this.setCognitoConfigObject();
    const params = {
      GroupName: 'ic-admin',
      UserPoolId: environment.userPoolId,
      Username: userName
    };

    const cognito = new CognitoIdentityProvider({ region: environment.region, credentials: value.credentials });
    cognito.adminAddUserToGroup(params, (err, data) => {
      if (err) {
        this.errorLog = 'admin change error ' + JSON.stringify(err);
        return callback(err, null);
      }
      else {
        this.successLog = 'admin change success ' + JSON.stringify(data);
        return callback(null, data);
      }
    });
  }
  auditLogOutActivity(eventMsg) {
    let auditLogTime = new Date();
    let action = {
      "dateTime": auditLogTime,
      "userName": this.casedashboardService.loggedInUserName,
      "action": eventMsg,
      "caseId": localStorage.getItem("caseId"),
      "eventOutCome": 1,
      "eventType": EventType.read
    };
    this.casedashboardService.auditLogActivity(action).subscribe({
      next: Response => {
        let result = Response;
        this.actionActivityDetails = result;
      },
      error: error => {
        this.errUserDetails = error;
      }
    })
  }
    /**
   * (description): This method is used to show the error message in popup
   * (memberof) : AuthService
   */
  showAPIErrorMsg(msg: string) {
    let dialogRef = this.dialog.open(ApiErrorPopUpComponent, {
      autoFocus: false,
      data: {errMsg: msg},
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  /**
   * (description): This method is used to get the SSM Parameters
   * (returns): Promise<any>
   * (memberof): AuthService
   */
  public async getSsmParameters(key: string, withDecryption = true): Promise<any> {
    try {
      const credentials = fromCognitoIdentityPool({
        identityPoolId: environment.identityPoolId,
        logins: {
          [environment.userPoolAwsLogin]: this.getCognitoIdTokenFromStorage(),
        },
        clientConfig: {
          region: environment.region,
        }
      });

      const ssmClient = new SSMClient({ region: environment.region, credentials });

      const params = {
        Name: key,
        WithDecryption: withDecryption,
      };

      const data = await ssmClient.send(new GetParameterCommand(params));
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  }
  /**
   * (description): To get the ssm parameters
   * (memberof) : AuthService
   */
  public async getSSMValue(key): Promise<any> {
    try {
      return (await this.getSsmParameters(key)).Parameter.Value;
    } catch (err) {
    }
  }
}
